import { Builder, BuilderComponent, builder } from '@builder.io/react';
import { graphql } from 'gatsby';
import { Suspense } from 'react';
import customComponentsRegistration, { richTextModels } from 'templates/componentGenerator';

import ModalContainer from 'molecules/Modal/Modal';

import Footer from 'components/Footer/Footer';
import Header from 'components/Header/Header';
import CaseStudyHero from 'components/Hero/HeroCaseStudy/HeroCaseStudy';
import Main from 'components/Main';
import SEO from 'components/seo';

import type { Query } from 'graphqlTypes';
import type { FC } from 'react';
import type { IHead } from 'templates/BuilderPage';

builder.init(process.env.GATSBY_BUILDER_KEY || '');

const CaseStudyTemplate = (query: { data: Query }) => {
  const {
    data: {
      allBuilderModels: {
        oneCaseStudy,
        oneGlobalBanner,
        oneComponentHeader: header,
        oneComponentFooter: footer,
        hiddenFormFields,
      },
    },
  } = query;

  const caseStudy = oneCaseStudy?.content;
  const { banner } = { ...oneCaseStudy?.data };
  const globalBanner = oneGlobalBanner?.data;

  const isCustomBanner = banner?.customBanner === true;
  const isGlobalBanner = globalBanner?.showBanner === true;
  const isHideBanner = banner?.hideBanner === true;

  customComponentsRegistration();

  return (
    <Suspense fallback>
      {header?.content && (
        <Header
          {...header.content.data}
          banner={(isCustomBanner && !isHideBanner && banner) || (isGlobalBanner && !isHideBanner && globalBanner)}
        />
      )}
      <Main>
        <CaseStudyHero study={caseStudy} />
        <BuilderComponent
          model="case-study"
          data={{ template: 'caseStudy', hiddenFields: JSON.stringify({ hiddenFormFields }) }}
          content={caseStudy}
          options={{ includeRefs: true, cachebust: true }}
        />
      </Main>
      {footer?.content && <Footer isDark={true} content={footer.content.data} />}
      <ModalContainer />
    </Suspense>
  );
};

export default CaseStudyTemplate;

export const Head: FC<IHead> = ({
  data: {
    allBuilderModels: { oneCaseStudy },
  },
  location,
}) => (
  <>
    {oneCaseStudy && (
      <SEO
        title={oneCaseStudy?.data?.metaTitle || oneCaseStudy?.data?.title}
        description={oneCaseStudy?.data?.metaTitle || oneCaseStudy?.data?.description}
        image={oneCaseStudy?.data?.featuredImage}
        location={location}
        indexable={!oneCaseStudy?.data?.noIndex || true}
      />
    )}
  </>
);

export const caseStudyQuery = graphql`
  query getCaseStudies($path: String!) {
    allBuilderModels {
      oneCaseStudy(target: { urlPath: $path }, options: { includeRefs: true, cachebust: true }) {
        content
        data {
          title
          description
          featuredImage
          url
          noIndex
          banner
          metaTitle
          metaDescription
        }
      }
      oneGlobalBanner(
        options: { includeRefs: true, cachebust: true }
        target: { id: "b241c59d043b4cc7b0f608356bea42ef" }
      ) {
        data {
          showBanner
          body
          button
          heading
          includeIcon
        }
      }
      oneComponentHeader(
        options: { includeRefs: true, cachebust: true }
        target: { id: "8fe1129198a9478c9cc7346a40a63805" }
      ) {
        content
      }
      oneComponentFooter(
        options: { includeRefs: true, cachebust: true }
        target: { id: "a7c491a16336403c94432e7b3ebb419b" }
      ) {
        content
      }
      hiddenFormFields {
        data {
          hiddenFormField
        }
      }
    }
  }
`;

Builder.register('insertMenu', {
  name: 'Case Studies',
  advanced: true,
  items: richTextModels.map(model => ({ name: model.config.name })),
});
